import React, { useEffect, useContext, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { v4 as uuidv4 } from 'uuid'

import OrganizationsContext from 'containers/organizations/organizationsContext'
import ScreenLoading from 'components/ScreenLoading'
import Item from 'components/Item'
import PopUpMessage from 'components/PopUpMessage'
import useListManualExpenses from 'containers/expenses/useListManualExpenses'
import useDeleteManualExpense from 'containers/expenses/useDeleteManualExpense'
import useImportManualExpenses, {
  ManualExpenseImportMode,
} from 'containers/expenses/useImportManualExpenses'
import useConfirmation from 'containers/confirmation/useConfirmation'

import './ManualExpensesList.scss'

const IMPORT_COLUMNS_HINT =
  'CSV columns: currency, category, payment_date, amount (required); ' +
  'subcategory, is_marketing_related, description, start_date, end_date, service_id (optional). ' +
  'Dates as YYYY-MM-DD. start_date and end_date go together, end after start.'

const IMPORT_MODE_HINT =
  'Import adds rows: the file is appended to the expenses already ' +
  'recorded. Import replaces all: the recorded expenses are deleted and ' +
  'the file becomes the full list — use this when re-uploading an edited ' +
  'export, so rows are not duplicated. Replacing re-creates every row, so ' +
  'links to individual expenses, their created date and who recorded them ' +
  'are not preserved.'

const CSV_HEADER =
  'currency,category,subcategory,payment_date,amount,is_marketing_related,description,start_date,end_date,service_id'

const IMPORT_TEMPLATE_CSV =
  CSV_HEADER +
  '\n' +
  'EUR,Marketing,Influencers,2026-06-15,1200.50,1,June influencer campaign,2026-06-01,2026-06-30,\n' +
  'USD,Operations,Software,2026-07-01,99.99,0,SaaS subscription,,,\n'

function downloadCsv(filename: string, content: string) {
  // Excel on Windows reads a BOM-less .csv as ANSI and mangles anything
  // non-ASCII (Greek categories, accented descriptions). The import decodes
  // utf-8-sig, so the BOM is stripped straight back off on re-upload.
  const blob = new Blob(['﻿' + content], {
    type: 'text/csv;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  // Attach before clicking and revoke on the next tick: revoking synchronously
  // can cancel the download in Firefox, which matters now that the same helper
  // serves a full export rather than the two-row template.
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

function csvField(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}

// Free-text fields are the ones a user can start with =, +, - or @, which Excel
// evaluates as a formula on open. A leading tab makes Excel read the cell as
// text, and the import strips surrounding whitespace, so the value still
// round-trips unchanged. Only applied to text columns: amounts are emitted by
// us and a negative one must stay a number in the spreadsheet.
function csvText(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? '\t' + value : value
}

// Same columns and value formats as the import endpoint expects, so an
// exported file round-trips through Import CSV unchanged.
function expensesToCsv(expenses: any[]): string {
  const rows = expenses.map((expense) =>
    [
      expense.currency.code,
      csvText(String(expense.category ?? '')),
      csvText(String(expense.subcategory ?? '')),
      expense.payment_date,
      (expense.amount / 100).toFixed(2),
      expense.is_marketing_related ? '1' : '0',
      csvText(String(expense.description ?? '')),
      expense.start_date || '',
      expense.end_date || '',
      expense.service ? String(expense.service.id) : '',
    ]
      .map((field) => csvField(String(field ?? '')))
      .join(',')
  )
  return CSV_HEADER + '\n' + rows.join('\n') + '\n'
}

function shorten(text: string, maxLength: number): string {
  if (text.length > maxLength) {
    return text.slice(0, maxLength - 3) + '...'
  } else {
    return text
  }
}

const ManualExpensesList = () => {
  const { currentOrganization, isLoadingOrganization } =
    useContext(OrganizationsContext)
  const currentOrganizationId = currentOrganization.id
  // The loader resolves to undefined, not [], while the organization id is
  // still unknown (first visit to an org in this browser), so never read this
  // list without the fallback.
  const [
    loadedExpenses,
    fetchExpenses,
    fetchExpensesStatus,
    fetchExpensesError,
  ] = useListManualExpenses(currentOrganizationId)
  const expenses: any[] = loadedExpenses ?? []
  const [deleteManualExpense, , deleteExpenseError] =
    useDeleteManualExpense(currentOrganizationId)
  const [importExpenses, importStatus, importError, importResponse] =
    useImportManualExpenses(currentOrganizationId)
  const [importMessage, setImportMessage] = useState('')
  const [importMode, setImportMode] = useState<ManualExpenseImportMode>('add')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const history = useHistory()
  const { confirm } = useConfirmation()

  useEffect(() => {
    fetchExpenses()
    // eslint-disable-next-line
  }, [currentOrganizationId])

  const handleDeleteExpense = async (uuid: string) => {
    await deleteManualExpense(uuid)
    fetchExpenses()
  }

  const handleAddExpense = () => {
    const newUuid = uuidv4()
    history.push(
      `/organization/${currentOrganization.slug}/expenses/${newUuid}`
    )
  }

  const handleImportFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }
    // Always ask, never gate on expenses.length: the list is [] both while the
    // fetch is in flight and after it fails, and in neither case does an empty
    // list mean the organization has nothing to delete.
    if (importMode === 'replace') {
      const counted =
        fetchExpensesStatus === 'fulfilled'
          ? `all ${expenses.length} recorded expenses`
          : 'every expense recorded for this organization'
      const confirmed = await confirm(
        `Replace ${counted} with the contents of "${file.name}"? ` +
          `The expenses being replaced will be deleted, and this ` +
          `cannot be undone.`,
        { confirmLabel: 'Replace all' }
      )
      if (!confirmed) {
        return
      }
    }
    const result = await importExpenses({ file, mode: importMode })
    if (result?.success) {
      setImportMessage(
        result.replaced > 0
          ? `Replaced ${result.replaced} expenses with ` +
              `${result.imported} from the file.`
          : `Imported ${result.imported} expenses successfully.`
      )
      fetchExpenses()
    }
  }

  const importErrors: string[] =
    importStatus === 'rejected'
      ? (importResponse as any)?.data?.detail?.errors || [importError]
      : []

  return (
    <div className="clusters-campaigns container-column manual-expenses">
      {deleteExpenseError && (
        <div className="alert alert-danger" role="alert">
          {deleteExpenseError}
        </div>
      )}
      {fetchExpensesError && (
        <div className="alert alert-danger" role="alert">
          {fetchExpensesError}
        </div>
      )}
      {importErrors.length > 0 && (
        <div className="alert alert-danger" role="alert">
          {importErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}
      <div className="head">
        <div className="page-name">
          <h2>Manual Expenses</h2>
          <span className="subtitle-text">
            Record expenses that you want included in your CPA metrics and
            reports, such as non-digital marketing expenses.
          </span>
          <div className="subtitle-text" style={{ marginTop: '10px' }}>
            Each expense is reported on its Payment Date. To spread it over
            time instead, such as a billboard on display for three months, give
            it a period Start and End Date: the amount is then split evenly per
            day from start to end.
          </div>
          <div className="subtitle-text" style={{ marginTop: '10px' }}>
            <a
              href="#template"
              onClick={(event) => {
                event.preventDefault()
                downloadCsv('manual_expenses_template.csv', IMPORT_TEMPLATE_CSV)
              }}
              title={IMPORT_COLUMNS_HINT}
            >
              Download the CSV import template
            </a>
          </div>
        </div>
      </div>
      <div className="head-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
        <button
          onClick={() =>
            downloadCsv(
              `manual_expenses_${currentOrganization.slug}.csv`,
              expensesToCsv(expenses)
            )
          }
          className="submit-button"
          disabled={expenses.length === 0}
          title="Download all recorded expenses as a CSV in the import format"
        >
          Download expenses
        </button>
        <select
          className="import-mode"
          value={importMode}
          onChange={(event) =>
            setImportMode(event.target.value as ManualExpenseImportMode)
          }
          disabled={importStatus === 'pending'}
          title={IMPORT_MODE_HINT}
        >
          <option value="add">Import adds rows</option>
          <option value="replace">Import replaces all</option>
        </select>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="submit-button"
          // Also blocked while the list is loading: a replace started then
          // would report a count it does not actually know.
          disabled={
            importStatus === 'pending' || fetchExpensesStatus === 'pending'
          }
          title={`${IMPORT_COLUMNS_HINT} ${IMPORT_MODE_HINT}`}
        >
          {importStatus === 'pending' ? 'Importing...' : 'Import CSV'}
        </button>
        <button onClick={handleAddExpense} className="submit-button">
          <span className="enlarge">+</span>Add expense
        </button>
      </div>
      <>
        {isLoadingOrganization || fetchExpensesStatus === 'pending' ? (
          <ScreenLoading />
        ) : (
          <div id="expenses-table">
            <div className="column is-not-draggable table-title">
              <div className="flex-grow grid">
                {/* Spans must mirror the Item columns below (rows are independent
                    12-track grids; a 1-span date gets squeezed by long amounts) */}
                <div className="grid-column-2 subtitle-text">Payment Date</div>
                <div
                  className="grid-column-2 subtitle-text"
                  style={{ textAlign: 'right', paddingRight: 80 }}
                >
                  Amount
                </div>
                <div className="grid-column-2 subtitle-text">Category</div>
                <div className="grid-column-2 subtitle-text">
                  Is Marketing Related
                </div>
                <div className="grid-column-2 subtitle-text">Description</div>
              </div>
            </div>
            {expenses.map((expense: any, expenseIndex: number) => {
              return (
                <Item
                  key={expense.uuid}
                  // Labels render only on mobile, where the header row is
                  // hidden and rows stack into cards (ManualExpensesList.scss)
                  columns={[
                    {
                      label: 'Payment Date',
                      value: expense.payment_date,
                      size: 2,
                    },
                    {
                      label: 'Amount',
                      value: (expense.amount / 100).toLocaleString(undefined, {
                        style: 'currency',
                        currency: expense.currency.code,
                      }),
                      size: 2,
                      valueStyle: { textAlign: 'right', paddingRight: 80 },
                    },
                    {
                      label: 'Category',
                      value:
                        expense.category +
                        (expense.subcategory
                          ? ' 〉 ' + expense.subcategory
                          : ''),
                      size: 2,
                    },
                    {
                      label: 'Marketing related',
                      value: expense.is_marketing_related ? 'Yes' : 'No',
                      size: 2,
                    },
                    {
                      label: 'Description',
                      value: shorten(expense.description, 20),
                      size: 2,
                    },
                  ]}
                  linkText="Edit Expense"
                  id={expense.uuid}
                  isDraggable={false}
                  editUrl={`/organization/${currentOrganization.slug}/expenses/${expense.uuid}`}
                  orderIndex={expenseIndex}
                  handleDeleteItem={() => handleDeleteExpense(expense.uuid)}
                  subtitle=""
                />
              )
            })}
          </div>
        )}
      </>
      {importMessage && (
        <PopUpMessage
          onClose={() => setImportMessage('')}
          message={importMessage}
          time={3000}
        />
      )}
      {localStorage.getItem('manualExpenseSaved') && (
        <PopUpMessage
          onClose={() => localStorage.removeItem('manualExpenseSaved')}
          message={`Manual expense was saved successfully.`}
          time={3000}
        />
      )}
    </div>
  )
}

export default React.memo(ManualExpensesList)
